import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Search, Edit, Trash2 } from "lucide-react";
import { Pagination } from "antd";
import CustomButton from "../../components/Buttons/CustomButton";
import { useThunk } from "../../hooks/useThunk";
import showToast from "../../utils/toastNotifications";

import ConfirmDeleteModal from "../../components/Modals/ConfirmDeleteModal";
import {
  deletePlaylist,
  getAllPlaylists,
} from "../../services/thunks/playlistThunks";
import { deleteFromCloudflare } from "../../services/thunks/cloudFlareThunks";

const ManagePlaylists = () => {
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();

  // Custom Hooks
  const [doGetAllPlaylists] = useThunk(getAllPlaylists);
  const [doDeletePlaylist, isDeletingPlaylist] = useThunk(deletePlaylist);
  const [doDeleteFromCloudflare, isDeletingFromCloudflare] =
    useThunk(deleteFromCloudflare);

  // Local States and Refs
  const [playlists, setPlaylists] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [playlistToDelete, setPlaylistToDelete] = useState(null);

  const searchTimeoutRef = useRef(null);
  const previousSearchRef = useRef("");

  const itemsPerPage = 10;

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const searchChanged = searchTerm !== previousSearchRef.current;
    previousSearchRef.current = searchTerm;

    const delay = searchChanged && searchTerm ? 500 : 0;

    searchTimeoutRef.current = setTimeout(() => {
      if (searchChanged && searchTerm && currentPage !== 1) {
        setCurrentPage(1);
        fetchPlaylists(1, searchTerm);
      } else {
        fetchPlaylists(currentPage, searchTerm);
      }
    }, delay);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, currentPage]);

  const fetchPlaylists = async (page = 1, search = "") => {
    const response = await doGetAllPlaylists({
      token: auth.token,
      page,
      limit: itemsPerPage,
      search: search.trim(),
    });

    if (response.success) {
      setPlaylists(response.response.data.playlists || []);
      setTotalItems(response.response.data.pagination?.totalItems || 0);
    } else {
      setPlaylists([]);
      setTotalItems(0);
    }
  };

  const handleDelete = (playlist) => {
    setPlaylistToDelete(playlist);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const promises = [];

      const result = await doDeletePlaylist({
        token: auth.token,
        id: playlistToDelete._id,
      });

      if (result.success) {
        if (playlistToDelete.coverImage?.key) {
          promises.push(
            await doDeleteFromCloudflare({
              key: playlistToDelete.coverImage.key,
              type: "images",
            })
          );
        }
        await Promise.all(promises);
        
        // Refresh the current page
        await fetchPlaylists(currentPage, searchTerm);
        
        setShowDeleteModal(false);
        setPlaylistToDelete(null);
        showToast("success", "Playlist deleted successfully");
      } else {
        showToast("error", "Failed to delete playlist");
      }
    } catch (error) {
      showToast("error", "An error occurred during deletion");
    }
  };

  // Handle pagination change
  const handlePaginationChange = (page, pageSize) => {
    setCurrentPage(page);
  };

  return (
    <div className="min-h-screen p-6 text-white">
      <div className="flex justify-between items-center mb-6">
        <div className="relative flex-1 border-lg border-[#808080] pr-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by title or ID"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            className="w-full bg-[#45444433] border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-[#808080] focus:outline-none focus:border-cyan-500"
          />
        </div>
        <CustomButton
          useGradientBorder
          rounded="rounded-lg"
          textSize="text-[18px] leading-[24px]"
          padding="py-[10.5px] px-[13.5px]"
          font="font-public-sans"
          border="border-lg"
          type="button"
          width="w-[178px]"
          onClick={() => navigate("/manage-playlist-add-new-playlist")}
        >
          + Add New Playlist
        </CustomButton>
      </div>

      <div className="bg-gray-800 rounded-[8px] overflow-x-auto border-[1px] border-[#dde1e6]">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#0D141B]">
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF]">
                ID
              </th>
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF]">
                Title
              </th>
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF]">
                Category
              </th>
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF]">
                Tracks
              </th>
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF]">
                Published
              </th>
              <th className="text-left py-4 px-6 font-[600] font-inter text-[14px] text-[#FFFFFF]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {playlists.map((playlist, index) => (
              <tr
                key={playlist._id}
                className={`border-b border-gray-700 ${
                  index % 2 === 0 ? "bg-[#1E1E1E]" : "bg-[#191919]"
                }`}
              >
                <td className="py-4 px-6 text-[#E2E2E2] text-[14px] font-Public_Sans font-[400]">
                  {playlist.playlistId}
                </td>
                <td className="py-4 px-6 text-[#E2E2E2] text-[14px] font-Public_Sans font-[400]">
                  {playlist.title}
                </td>
                <td className="py-4 px-6 text-[#E2E2E2] text-[14px] font-Public_Sans font-[400]">
                  {playlist.subcategory?.name}
                </td>
                <td className="py-4 px-6 text-[#E2E2E2] text-[14px] font-Public_Sans font-[400]">
                  {playlist.tracksCount}
                </td>
                <td className="py-4 px-6 text-[#E2E2E2] text-[14px] font-Public_Sans font-[400]">
                  {new Date(playlist.publishedDate).toLocaleDateString(
                    "en-US",
                    {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    }
                  )}
                </td>
                <td className="py-4 px-6">
                  <div className="flex gap-2">
                    <button
                      className="flex items-center justify-center gap-2 px-4 py-1 border border-[#B4C007] text-[#B4C007] rounded-[15px] text-sm w-[90px] hover:bg-[#333300] transition"
                      onClick={() => navigate(`/edit-playlist/${playlist._id}`)}
                    >
                      <Edit className="w-3 h-3" />
                      Edit
                    </button>
                    <button
                      className="flex items-center justify-center gap-2 px-4 py-1 border border-[#C00707] text-[#C00707] rounded-[15px] text-sm w-[90px] hover:bg-[#330000] transition"
                      onClick={() => handleDelete(playlist)}
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "16px",
        }}
      >
        <Pagination
          current={currentPage}
          pageSize={itemsPerPage}
          total={totalItems}
          onChange={handlePaginationChange}
          showSizeChanger={false}
          className="custom-pagination"
        />
      </div>

      {showDeleteModal && (
        <ConfirmDeleteModal
          visible={showDeleteModal}
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
          title="Confirm Deletion"
          content={`Are you sure you want to delete the playlist "${playlistToDelete?.title}"?`}
          disabled={isDeletingPlaylist || isDeletingFromCloudflare}
          loading={isDeletingPlaylist || isDeletingFromCloudflare}
        />
      )}
    </div>
  );
};

export default ManagePlaylists;
